import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { type Project, useAuth } from '@/lib/auth';
import { bpConfirm } from '@/lib/dialogs';
import { Loader2, Plus, Search, X } from '@/lib/icons';
import {
  addIssueLink,
  createTask,
  isTaskDuplicateSuggestions,
  listStatuses,
  listTasks,
  type Task,
  type TaskStatus,
} from '@/lib/tasksApi';

// TaskDetailPopup transitively pulls IssueDetailPanel + TaskDetailDrawer +
// RichEditor + vendor-tiptap. Eager-loading it dragged ~500 KB of tiptap
// into the initial bundle via this static chain; the popup is only ever
// rendered when the user opens a task, so it's cheap to defer.
const TaskDetailPopup = lazy(() =>
  import('@/components/tasks/TaskDetailPopup').then((m) => ({ default: m.TaskDetailPopup })),
);

import { type LinkedIssueRef, resolveIssueLinkInput } from '@/components/tasks/issueLinkInput';
import { useDebouncedValue } from '@/lib/useDebouncedValue';

const OPEN_CAPTURE_EVENT = 'bp:open-task-capture';
const TASK_CREATED_EVENT = 'bp:task-created';

const ROUTE_PROJECT_KEYS: Array<[RegExp, string]> = [
  [/^\/crashes\/fingerprint-settings(?:\/|$)/, 'issue-rules'],
  [/^\/issues\/workflow(?:\/|$)/, 'issue-workflow'],
  [/^\/issues\/rules(?:\/|$)/, 'issue-rules'],
  [/^\/game-config(?:\/|$)/, 'game-config'],
  [/^\/debug-tools(?:\/|$)/, 'debug-tools'],
  [/^\/performance(?:\/|$)/, 'performance'],
  [/^\/analytics(?:\/|$)/, 'analytics'],
  [/^\/artifacts(?:\/|$)/, 'artifacts'],
  [/^\/feedback(?:\/|$)/, 'feedback'],
  [/^\/devices(?:\/|$)/, 'devices'],
  [/^\/scripts(?:\/|$)/, 'scripts'],
  [/^\/issues(?:\/|$)/, 'issues'],
  [/^\/tasks(?:\/|$)/, 'tasks'],
  [/^\/chats(?:\/|$)/, 'chat'],
  [/^\/logs(?:\/|$)/, 'logs'],
  [/^\/releases(?:\/|$)/, 'releases'],
  [/^\/$/, 'releases'],
];

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  if (
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.tagName === 'SELECT' ||
    el.isContentEditable
  )
    return true;
  // Monaco / CodeMirror / other custom editors don't always surface a
  // <textarea> as event.target — match by role + ancestor class.
  if (el.getAttribute('role') === 'textbox') return true;
  if (
    el.closest('.monaco-editor') ||
    el.closest('.cm-editor') ||
    el.closest('[contenteditable="true"]')
  )
    return true;
  return false;
}

function anyDialogOpen(): boolean {
  return (
    typeof document !== 'undefined' &&
    !!document.querySelector(
      '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
    )
  );
}

function pageProjectKey(pathname: string): string | null {
  for (const [pattern, key] of ROUTE_PROJECT_KEYS) {
    if (pattern.test(pathname)) return key;
  }
  return null;
}

function routeProjectId(pathname: string): string | null {
  const match = pathname.match(/^\/project\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function issueIdFromLocation(pathname: string, search: string): string | null {
  if (!pathname.startsWith('/issues')) return null;
  return new URLSearchParams(search).get('selected');
}

function contextProjectId(pathname: string, projects: Project[]): string | null {
  const direct = routeProjectId(pathname);
  if (direct) return direct;

  const key = pageProjectKey(pathname);
  if (!key || typeof window === 'undefined') return null;

  const saved = localStorage.getItem(`project_filter_${key}`);
  if (saved !== null) return saved || null;

  return projects[0]?.id ?? null;
}

function initials(name: string | null | undefined): string {
  const value = (name || '').trim();
  if (!value) return '?';
  return (
    value
      .split(/[\s._-]+/)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('')
      .slice(0, 2) || '?'
  );
}

type ProjectTaskGroup = {
  project: Project | null;
  projectId: string;
  tasks: Task[];
};

function groupTasksByProject(tasks: Task[], projects: Project[]): ProjectTaskGroup[] {
  const projectById = new Map(projects.map((project) => [project.id, project]));
  const seen = new Set<string>();
  const groupsById = new Map<string, Task[]>();
  for (const task of tasks) {
    if (!groupsById.has(task.ProjectId)) groupsById.set(task.ProjectId, []);
    groupsById.get(task.ProjectId)!.push(task);
  }

  const groups: ProjectTaskGroup[] = [];
  for (const project of projects) {
    const items = groupsById.get(project.id);
    if (!items?.length) continue;
    seen.add(project.id);
    groups.push({ project, projectId: project.id, tasks: items });
  }
  for (const [projectId, items] of groupsById.entries()) {
    if (seen.has(projectId)) continue;
    groups.push({ project: projectById.get(projectId) ?? null, projectId, tasks: items });
  }
  return groups;
}

export function GlobalTaskControls() {
  const location = useLocation();
  const { projects } = useAuth();
  const isTasksRoute = /^\/tasks(?:\/|$)/.test(location.pathname);
  const [projectVersion, setProjectVersion] = useState(0);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 180);
  const [results, setResults] = useState<Task[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const [captureOpen, setCaptureOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [captureProjectId, setCaptureProjectId] = useState('');
  const [linkInput, setLinkInput] = useState('');
  const [captureTargetPlatform, setCaptureTargetPlatform] = useState('');
  const [creating, setCreating] = useState(false);
  const titleInputRef = useRef<HTMLInputElement | null>(null);

  const [drawerTaskId, setDrawerTaskId] = useState<string | null>(null);
  const [drawerProjectId, setDrawerProjectId] = useState<string | null>(null);
  const [drawerStatuses, setDrawerStatuses] = useState<TaskStatus[]>([]);
  const [drawerLinkedIssue, setDrawerLinkedIssue] = useState<LinkedIssueRef | null>(null);

  useEffect(() => {
    const onProjectChange = () => setProjectVersion((v) => v + 1);
    window.addEventListener('bp:projectfilterchange', onProjectChange);
    window.addEventListener('storage', onProjectChange);
    return () => {
      window.removeEventListener('bp:projectfilterchange', onProjectChange);
      window.removeEventListener('storage', onProjectChange);
    };
  }, []);

  const activeProjectId = useMemo(
    () => contextProjectId(location.pathname, projects),
    [location.pathname, projects, projectVersion],
  );
  const currentIssueId = useMemo(
    () => issueIdFromLocation(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const groupedResults = useMemo(() => groupTasksByProject(results, projects), [projects, results]);

  useEffect(() => {
    const onOpenCapture = () => setCaptureOpen(true);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isTypingTarget(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.toLowerCase() !== 'c') return;
      // Don't fire while another dialog/drawer is open — dialog focus traps
      // make event.target a button or the dialog content div, neither of
      // which isTypingTarget catches.
      if (anyDialogOpen()) return;
      event.preventDefault();
      if (isTasksRoute) return;
      setCaptureOpen(true);
    };
    window.addEventListener(OPEN_CAPTURE_EVENT, onOpenCapture);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener(OPEN_CAPTURE_EVENT, onOpenCapture);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isTasksRoute]);

  useEffect(() => {
    if (!captureOpen) return;
    setTitle('');
    setCaptureProjectId(activeProjectId ?? projects[0]?.id ?? '');
    setLinkInput(currentIssueId ?? '');
    requestAnimationFrame(() => titleInputRef.current?.focus());
  }, [activeProjectId, captureOpen, currentIssueId, projects]);

  useEffect(() => {
    const q = debouncedSearch.trim();
    if (!q) {
      setResults([]);
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);
    listTasks({ q, limit: 80 })
      .then(({ items }) => {
        if (cancelled) return;
        setResults(items.slice(0, 40));
        setSearchOpen(true);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setResults([]);
          toast.error(`Task search failed: ${e instanceof Error ? e.message : e}`);
        }
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, projectVersion]);

  useEffect(() => {
    if (!drawerProjectId) {
      setDrawerStatuses([]);
      return;
    }
    let cancelled = false;
    listStatuses({ projectId: drawerProjectId })
      .then((r) => {
        if (!cancelled) setDrawerStatuses(r.items);
      })
      .catch(() => {
        if (!cancelled) setDrawerStatuses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [drawerProjectId]);

  const openTask = useCallback((task: Task) => {
    setDrawerTaskId(task.Id);
    setDrawerProjectId(task.ProjectId);
    setDrawerLinkedIssue(null);
    setSearchOpen(false);
  }, []);

  const openTaskById = useCallback((id: string) => {
    setDrawerTaskId(id);
    setDrawerLinkedIssue(null);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerTaskId(null);
    setDrawerProjectId(null);
    setDrawerStatuses([]);
    setDrawerLinkedIssue(null);
  }, []);

  const submitCapture = useCallback(
    async (openAfterCreate: boolean) => {
      const cleanTitle = title.trim();
      if (!cleanTitle) {
        toast.error('Title is required');
        return;
      }
      if (!captureProjectId) {
        toast.error('Project is required');
        return;
      }

      setCreating(true);
      try {
        const linkRaw = linkInput.trim();
        const tp = captureTargetPlatform || null;
        let created = await createTask(
          { title: cleanTitle, checkDuplicates: true, targetPlatform: tp },
          { projectId: captureProjectId },
        );
        if (isTaskDuplicateSuggestions(created)) {
          const first = created.Suggestions[0];
          const useExisting =
            first &&
            (await bpConfirm(
              `Similar task found:\n\n"${first.Title}"\n\nUse this existing task instead?\nCancel creates this new task anyway.`,
              { okLabel: 'Use existing', cancelLabel: 'Create new' },
            ));
          if (useExisting && first) {
            if (linkRaw) {
              try {
                const link = await resolveIssueLinkInput(linkRaw);
                if (link) await addIssueLink(first.Id, link.type, link.id);
              } catch (e) {
                toast.warning(
                  `Existing task opened without link: ${e instanceof Error ? e.message : e}`,
                );
              }
            }
            window.dispatchEvent(
              new CustomEvent(TASK_CREATED_EVENT, {
                detail: { taskId: first.Id, projectId: first.ProjectId },
              }),
            );
            toast.success('Using existing task');
            setCaptureOpen(false);
            if (openAfterCreate) openTask(first);
            return;
          }
          created = await createTask(
            { title: cleanTitle, force: true, targetPlatform: tp },
            { projectId: captureProjectId },
          );
        }

        if (linkRaw) {
          try {
            const link = await resolveIssueLinkInput(linkRaw);
            if (link) await addIssueLink(created.Id, link.type, link.id);
          } catch (e) {
            toast.warning(`Task created without link: ${e instanceof Error ? e.message : e}`);
          }
        }

        window.dispatchEvent(
          new CustomEvent(TASK_CREATED_EVENT, {
            detail: { taskId: created.Id, projectId: created.ProjectId },
          }),
        );
        toast.success('Task created');
        setCaptureOpen(false);

        if (openAfterCreate) openTask(created);
      } catch (e) {
        toast.error(`Create failed: ${e instanceof Error ? e.message : e}`);
      } finally {
        setCreating(false);
      }
    },
    [captureProjectId, linkInput, openTask, title],
  );

  return (
    <>
      <div className="relative w-32 sm:w-52 md:w-72">
        <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => {
            if (search.trim()) setSearchOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setSearchOpen(false);
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
          placeholder="Search tasks"
          className="h-8 pl-7 pr-8 text-sm"
        />
        {searchLoading ? (
          <Loader2 className="absolute right-2 top-1/2 size-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
        ) : search ? (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setResults([]);
              setSearchOpen(false);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear task search"
          >
            <X className="size-3.5" />
          </button>
        ) : null}

        {searchOpen && search.trim() && (
          <div className="absolute right-0 top-full z-50 mt-1 w-[24rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-xl">
            {results.length === 0 && !searchLoading ? (
              <div className="px-3 py-3 text-sm text-muted-foreground">No matching tasks</div>
            ) : (
              <ul className="max-h-96 overflow-y-auto py-1">
                {groupedResults.map((group) => (
                  <li key={group.projectId} className="border-t first:border-t-0">
                    <div className="sticky top-0 z-10 flex items-center justify-between bg-popover px-3 py-1.5 text-[11px] font-medium uppercase tracking-normal text-muted-foreground">
                      <span className="min-w-0 truncate">
                        {group.project?.name ?? group.projectId}
                      </span>
                      <span className="ml-3 shrink-0 font-mono">{group.tasks.length}</span>
                    </div>
                    <ul>
                      {group.tasks.map((task) => (
                        <li key={task.Id}>
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => openTask(task)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/70"
                          >
                            <span
                              className="size-2 shrink-0 rounded-full"
                              style={{
                                backgroundColor: task.Status?.Color ?? 'rgba(255,255,255,0.25)',
                              }}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm">{task.Title}</span>
                              <span className="block truncate text-[11px] text-muted-foreground">
                                {task.Status?.Name ?? 'No status'}
                              </span>
                            </span>
                            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
                              {initials(task.AssigneeName)}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {!isTasksRoute && (
        <Button
          size="icon"
          variant="ghost"
          onClick={() => setCaptureOpen(true)}
          aria-label="Create task"
          title="Create task"
        >
          <Plus className="size-3.5" />
        </Button>
      )}

      <Dialog
        open={captureOpen}
        onOpenChange={(open) => {
          if (!creating) setCaptureOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>New task</DialogTitle>
          </DialogHeader>

          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="global-task-title">Title</Label>
              <Input
                id="global-task-title"
                ref={titleInputRef}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void submitCapture(e.shiftKey);
                  }
                }}
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Project</Label>
              <Select
                value={captureProjectId}
                onValueChange={(value) => setCaptureProjectId(value ?? '')}
              >
                <SelectTrigger className="h-8 w-full">
                  <SelectValue placeholder="Project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="global-task-link">Link</Label>
              <Input
                id="global-task-link"
                value={linkInput}
                onChange={(e) => setLinkInput(e.target.value)}
                placeholder="issue id, URL, or type:id"
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Target platform</Label>
              <Select
                value={captureTargetPlatform || 'none'}
                onValueChange={(value) =>
                  setCaptureTargetPlatform(!value || value === 'none' ? '' : value)
                }
              >
                <SelectTrigger className="h-8 w-full">
                  <SelectValue placeholder="Target platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unspecified</SelectItem>
                  <SelectItem value="android">Android</SelectItem>
                  <SelectItem value="ios">iOS</SelectItem>
                  <SelectItem value="standalone">Standalone</SelectItem>
                  <SelectItem value="web">Web</SelectItem>
                  <SelectItem value="server">Server</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCaptureOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => void submitCapture(true)}
              disabled={creating || !title.trim() || !captureProjectId}
            >
              {creating ? 'Creating...' : 'Create & Open'}
            </Button>
            <Button
              onClick={() => void submitCapture(false)}
              disabled={creating || !title.trim() || !captureProjectId}
            >
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Only mount the popup chunk once a task is actually selected — the
          fallback is null because the popup itself renders a Dialog. */}
      {(drawerTaskId || drawerLinkedIssue) && (
        <Suspense fallback={null}>
          <TaskDetailPopup
            taskId={drawerTaskId}
            issueOnly={null}
            linkedIssue={drawerLinkedIssue}
            statuses={drawerStatuses}
            onOpenLinkedIssue={setDrawerLinkedIssue}
            onCloseLinkedIssue={() => setDrawerLinkedIssue(null)}
            onOpenTask={openTaskById}
            onClose={closeDrawer}
            onChanged={() => {
              if (debouncedSearch.trim()) {
                listTasks({ q: debouncedSearch.trim(), limit: 80 })
                  .then((r) => setResults(r.items.slice(0, 40)))
                  .catch(() => {
                    /* ignore */
                  });
              }
            }}
          />
        </Suspense>
      )}
    </>
  );
}
